import React, { useCallback } from 'react';
import { Paper, Title, Text, Stack, Button, Alert, Group, Box, Badge, Loader } from '@mantine/core';
import { IconBrandReddit, IconPlayerPlay, IconPlayerStop, IconInfoCircle } from '@tabler/icons-react';
import { RedditCrawlSectionProps } from './RedditCrawlSection.types';
import { RedditCrawlHistory } from './RedditCrawlHistory';
import { useStartRedditCrawlMutation, useStopRedditCrawlMutation } from '../../../../application/queries/adminOperationsQueries';
import { useCrawlStatusQuery } from '../../../../application/queries/crawlStatusQueries';
import { useAuth } from '../../../hooks/useAuth';

/**
 * RedditCrawlSection Component
 *
 * Admin-only section for manually controlling the Reddit crawler.
 * NOTE: Full implementation planned for Sessions 005/006.
 * Currently shows placeholder UI with non-functional start/stop buttons.
 *
 * Features (planned):
 * - Start/Stop crawler controls
 * - Real-time crawler status display
 * - Crawl history and statistics
 *
 * @example
 * <RedditCrawlSection />
 */
export const RedditCrawlSection: React.FC<RedditCrawlSectionProps> = (): React.ReactElement => {
  const { user } = useAuth();

  // Fetch crawler status (auto-refresh every 30 seconds)
  const { data: status, isLoading: isLoadingStatus } = useCrawlStatusQuery({
    refetchInterval: 30000,
  });

  // Mutation hooks for start/stop operations
  const startCrawl = useStartRedditCrawlMutation();
  const stopCrawl = useStopRedditCrawlMutation();

  /**
   * Handle start crawl button click
   */
  const handleStartCrawl = useCallback((): void => {
    if (!user?.role) {
      return;
    }

    startCrawl.mutate({ userRole: user.role });
  }, [user?.role, startCrawl]);

  /**
   * Handle stop crawl button click
   */
  const handleStopCrawl = useCallback((): void => {
    if (!user?.role) {
      return;
    }

    stopCrawl.mutate({ userRole: user.role });
  }, [user?.role, stopCrawl]);

  // Disable buttons while mutations are pending
  const isStartDisabled = startCrawl.isPending || stopCrawl.isPending;
  const isStopDisabled = startCrawl.isPending || stopCrawl.isPending;

  // Determine status badge color
  const getStatusColor = (statusValue: string): string => {
    switch (statusValue) {
      case 'Running':
        return 'blue';
      case 'Idle':
        return 'green';
      case 'Warning':
        return 'yellow';
      case 'Unknown':
        return 'gray';
      default:
        return 'gray';
    }
  };

  // Format last crawl time
  const formatLastCrawlTime = (timestamp: string | null): string => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  return (
    <Paper shadow="sm" p="lg" radius="md" withBorder>
      <Stack gap="md">
        {/* Section Header */}
        <Box>
          <Group gap="xs" mb="xs">
            <IconBrandReddit size={24} stroke={1.5} />
            <Title order={2}>Reddit Crawler Control</Title>
          </Group>
          <Text c="dimmed" size="sm">
            Manually start and stop the Reddit mention crawler
          </Text>
        </Box>

        {/* Info Alert - Not Yet Implemented */}
        <Alert
          icon={<IconInfoCircle size={16} />}
          title="Feature Coming Soon"
          color="blue"
          variant="light"
        >
          <Text size="sm">
            Manual crawl control will be fully implemented in Sessions 005/006.
            <br />
            The Reddit crawler currently runs automatically as a background service.
            <br />
            These buttons will return placeholder responses until the feature is complete.
          </Text>
        </Alert>

        {/* Control Buttons */}
        <Group>
          <Button
            color="green"
            leftSection={<IconPlayerPlay size={16} />}
            onClick={handleStartCrawl}
            disabled={isStartDisabled}
            loading={startCrawl.isPending}
          >
            {startCrawl.isPending ? 'Starting...' : 'Start Crawl'}
          </Button>

          <Button
            color="red"
            leftSection={<IconPlayerStop size={16} />}
            onClick={handleStopCrawl}
            disabled={isStopDisabled}
            loading={stopCrawl.isPending}
          >
            {stopCrawl.isPending ? 'Stopping...' : 'Stop Crawl'}
          </Button>
        </Group>

        {/* Status Display */}
        <Paper p="md" withBorder>
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="sm" fw={600}>
                Crawler Status:
              </Text>
              {isLoadingStatus ? (
                <Loader size="xs" />
              ) : status ? (
                <Badge color={getStatusColor(status.status)} variant="light">
                  {status.status}
                </Badge>
              ) : (
                <Text size="sm" c="dimmed">
                  Unknown
                </Text>
              )}
            </Group>

            {status && (
              <>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Active Sources:
                  </Text>
                  <Text size="sm" fw={500}>
                    {status.activeSourcesCount}
                  </Text>
                </Group>

                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Last Crawl:
                  </Text>
                  <Text size="sm" fw={500}>
                    {formatLastCrawlTime(status.lastCrawlTime)}
                  </Text>
                </Group>

                {status.runningCrawlsCount > 0 && (
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      Running Crawls:
                    </Text>
                    <Badge color="blue" variant="filled" size="sm">
                      {status.runningCrawlsCount}
                    </Badge>
                  </Group>
                )}

                {status.recentFailuresCount > 0 && (
                  <Group justify="space-between">
                    <Text size="sm" c="red">
                      Recent Failures (24h):
                    </Text>
                    <Badge color="red" variant="filled" size="sm">
                      {status.recentFailuresCount}
                    </Badge>
                  </Group>
                )}

                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Mentions Found (24h):
                  </Text>
                  <Text size="sm" fw={500} c="teal">
                    {status.mentionsFoundLast24Hours}
                  </Text>
                </Group>

                <Text size="xs" c="dimmed" mt="xs">
                  {status.message}
                </Text>
              </>
            )}
          </Stack>
        </Paper>

        {/* Crawl History */}
        <RedditCrawlHistory />
      </Stack>
    </Paper>
  );
};
